"use client";

import { Component, type ErrorInfo, type ReactNode } from "react";

import { AppErrorScreen } from "@/components/system/AppErrorScreen";

/**
 * Error boundary de CLASE, además del `error.tsx` de Next.
 *
 * Existe por una sola razón: `error.tsx` recibe `{ error, reset }` y nada más.
 * Un boundary de clase recibe también `errorInfo.componentStack`, que es **el
 * dato que resuelve el caso**. Sin él, un "Minified React error #185"
 * (`Maximum update depth exceeded`) sólo dice que algo entró en un lazo de
 * renders; con él, dice CUÁL es el componente.
 *
 * Es justo el reporte que no se podía conseguir: el crash pasa en el celular
 * del profe, con el build de producción, y no se reproduce en las máquinas de
 * desarrollo. Va envolviendo al árbol entero desde el layout raíz.
 *
 * Cuando este boundary atrapa, `error.tsx` ya no ve nada — y está bien: éste
 * muestra la misma pantalla, con más información.
 */
export class ClientErrorBoundary extends Component<
  { children: ReactNode },
  { error: (Error & { digest?: string }) | null; componentStack: string | null }
> {
  state: { error: (Error & { digest?: string }) | null; componentStack: string | null } = {
    error: null,
    componentStack: null,
  };

  static getDerivedStateFromError(error: Error & { digest?: string }) {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Aunque esté minificado, el stack de componentes conserva los nombres:
    // alcanza para señalar el componente que entró en lazo.
    this.setState({ componentStack: errorInfo.componentStack ?? null });
    console.error("[ClientErrorBoundary]", error, errorInfo.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <AppErrorScreen
          error={this.state.error}
          componentStack={this.state.componentStack}
          reset={() => this.setState({ error: null, componentStack: null })}
        />
      );
    }
    return this.props.children;
  }
}
